<template>
  <FlashNotifications ref="flash" />

  <!-- Disconnect confirmation modal -->
  <Transition name="modal">
    <div v-if="confirmModal.open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="confirmModal.open = false">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm"></div>
      <div class="relative bg-white dark:bg-[#1C2128] rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 max-w-sm w-full p-6 modal-card">
        <div class="w-12 h-12 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mx-auto mb-4">
          <svg class="w-6 h-6 text-red-500" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"/>
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-900 dark:text-white text-center mb-2">
          Disconnect {{ confirmModal.integration?.name }}?
        </h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
          {{ confirmModal.integration?.locale_name === 'zoom'
              ? 'Your event location will be changed to no location. This cannot be undone automatically.'
              : 'This integration will be removed from your account.' }}
        </p>
        <div class="flex gap-3">
          <button @click="confirmModal.open = false"
            class="flex-1 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button @click="executeDisconnect"
            :disabled="!!disconnecting"
            class="flex-1 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-lg transition-colors flex items-center justify-center gap-2">
            <svg v-if="disconnecting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- Page -->
  <div class="min-h-screen bg-gray-50 dark:bg-[#0d1117]">

    <!-- ══ MOBILE ══ -->
    <div class="sm:hidden">

      <!-- Sticky mobile header -->
      <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
        <div class="flex items-center justify-between px-4 h-14">
          <div class="flex items-center gap-2">
            <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Integrations</h1>
            <span v-if="!store.loading && store.connectedCount > 0"
              class="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
              {{ store.connectedCount }}
            </span>
          </div>
        </div>

        <!-- Search -->
        <div class="px-4 pb-3">
          <div class="relative">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
            <input v-model="searchQuery" type="search" placeholder="Search integrations..."
              class="w-full bg-gray-100 dark:bg-gray-800 rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-gray-200 placeholder-gray-400 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-700 transition-all"/>
          </div>
        </div>

        <!-- Category pills -->
        <div class="flex gap-2 px-4 pb-3.5 overflow-x-auto no-scrollbar">
          <button @click="selectedCategory = ''"
            class="flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
            :class="selectedCategory === ''
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
            All
          </button>
          <button v-for="cat in uniqueCategories" :key="cat.key"
            @click="selectedCategory = cat.key"
            class="flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all"
            :class="selectedCategory === cat.key
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'">
            {{ cat.label }}
          </button>
        </div>
      </div>

      <!-- Mobile list -->
      <div class="px-4 pt-4 pb-28 space-y-3">

        <!-- Skeleton -->
        <template v-if="store.loading">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 p-4 animate-pulse">
            <div class="flex items-start gap-3">
              <div class="w-11 h-11 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
              <div class="flex-1 space-y-2">
                <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/5"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-1/4"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
                <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-3/4"></div>
              </div>
              <div class="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0"></div>
            </div>
          </div>
        </template>

        <!-- Error -->
        <template v-else-if="store.error">
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-14 h-14 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</p>
            <p class="text-sm text-gray-400 dark:text-gray-500 mb-4">{{ store.error }}</p>
            <button @click="retry" class="text-blue-600 dark:text-blue-400 font-semibold text-sm">Try again</button>
          </div>
        </template>

        <!-- Empty search result -->
        <template v-else-if="filteredIntegrations.length === 0">
          <div class="flex flex-col items-center justify-center py-20 text-center">
            <div class="w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
            </div>
            <p class="font-semibold text-gray-900 dark:text-white mb-1">No integrations found</p>
            <p class="text-sm text-gray-400 dark:text-gray-500 mb-4">Try a different search or category.</p>
            <button @click="clearFilters" class="text-blue-600 dark:text-blue-400 font-semibold text-sm">Clear filters</button>
          </div>
        </template>

        <!-- Mobile integration cards -->
        <template v-else>
          <div v-for="integration in filteredIntegrations" :key="integration.integration_id"
            class="bg-white dark:bg-[#1C2128] rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div class="p-4">
              <div class="flex items-start gap-3">
                <!-- Logo -->
                <div class="w-11 h-11 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img :src="integration.logo" :alt="integration.name" class="w-7 h-7 object-contain">
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 flex-wrap">
                    <p class="font-semibold text-gray-900 dark:text-white text-sm leading-tight">{{ integration.name }}</p>
                    <span v-if="integration.user_integration_id"
                      class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                      <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      Connected
                    </span>
                  </div>
                  <span class="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
                    :class="categoryColorClass(integration.category_key)">
                    {{ integration.category }}
                  </span>
                  <p class="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-relaxed">{{ integration.description }}</p>
                </div>
              </div>
            </div>
            <!-- Action row -->
            <div class="px-4 pb-4 flex justify-end">
              <component :is="actionButtonComponent(integration)"
                v-bind="actionButtonAttrs(integration)"
                @click="handleAction(integration)">
                <svg v-if="loadingId === integration.integration_id" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                <svg v-else-if="actionType(integration) === 'manage'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
                </svg>
                <span>{{ actionLabel(integration) }}</span>
              </component>
            </div>
          </div>
        </template>

      </div>
    </div>
    <!-- ══ END MOBILE ══ -->


    <!-- ══ DESKTOP ══ -->
    <div class="hidden sm:flex sm:flex-col sm:h-screen">

      <!-- Desktop sticky header -->
      <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">

        <!-- Title row -->
        <div class="flex items-center justify-between px-6 py-3.5">
          <div>
            <div class="flex items-center gap-2.5">
              <h1 class="text-sm font-semibold text-gray-900 dark:text-white leading-none">Integrations</h1>
              <span v-if="!store.loading && store.connectedCount > 0"
                class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 tabular-nums">
                {{ store.connectedCount }} connected
              </span>
            </div>
            <p class="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 leading-none">
              Connect third-party tools to extend your scheduling workflow.
            </p>
          </div>

          <!-- Search -->
          <div class="relative w-64">
            <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
            </svg>
            <input v-model="searchQuery" type="search" placeholder="Search integrations..."
              class="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/80 text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"/>
          </div>
        </div>

        <!-- Category tabs row -->
        <div class="flex items-center gap-0 px-4 border-t border-gray-100 dark:border-gray-700/60">
          <button @click="selectedCategory = ''"
            class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap"
            :class="selectedCategory === ''
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'">
            All
            <span class="ml-1.5 text-[11px] font-semibold tabular-nums"
              :class="selectedCategory === '' ? 'text-blue-400 dark:text-blue-500' : 'text-gray-400 dark:text-gray-600'">
              {{ store.integrations.length }}
            </span>
          </button>
          <button v-for="cat in uniqueCategories" :key="cat.key"
            @click="selectedCategory = cat.key"
            class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap"
            :class="selectedCategory === cat.key
              ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'">
            {{ cat.label }}
          </button>
        </div>
      </div>

      <!-- Desktop scrollable content -->
      <div class="flex-1 overflow-y-auto px-6 py-6">

        <!-- Loading skeleton -->
        <template v-if="store.loading">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="i in 6" :key="i"
              class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden animate-pulse">
              <div class="p-5 space-y-4">
                <div class="flex items-start gap-4">
                  <div class="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 flex-shrink-0"></div>
                  <div class="flex-1 space-y-2 pt-1">
                    <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                    <div class="h-4 bg-gray-100 dark:bg-gray-800 rounded w-1/3"></div>
                  </div>
                </div>
                <div class="space-y-1.5">
                  <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
                  <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-4/5"></div>
                </div>
              </div>
              <div class="border-t border-gray-100 dark:border-gray-700 px-5 py-3">
                <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-28"></div>
              </div>
            </div>
          </div>
        </template>

        <!-- Error state -->
        <template v-else-if="store.error">
          <div class="flex flex-col items-center justify-center py-32 text-center px-4">
            <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">Something went wrong</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ store.error }}</p>
            <button @click="retry"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
              Try again
            </button>
          </div>
        </template>

        <!-- Integrations grid -->
        <template v-else>
          <!-- No results -->
          <div v-if="filteredIntegrations.length === 0"
            class="flex flex-col items-center justify-center py-24 text-center">
            <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
              </svg>
            </div>
            <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">No integrations found</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">Try a different search or category.</p>
            <button @click="clearFilters"
              class="px-4 py-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors">
              Clear filters
            </button>
          </div>

          <!-- Grid -->
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="integration in filteredIntegrations" :key="integration.integration_id"
              class="group bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-md dark:hover:shadow-gray-900/40 transition-all duration-200 flex flex-col overflow-hidden">

              <div class="p-5 flex-1 flex flex-col">
                <!-- Logo + name -->
                <div class="flex items-start gap-4 mb-3">
                  <div class="w-12 h-12 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img :src="integration.logo" :alt="integration.name" class="w-8 h-8 object-contain">
                  </div>
                  <div class="flex-1 min-w-0 pt-0.5">
                    <div class="flex items-center gap-2 flex-wrap">
                      <p class="font-semibold text-gray-900 dark:text-white text-[15px] leading-tight">{{ integration.name }}</p>
                      <span v-if="integration.user_integration_id"
                        class="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                        <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                        Connected
                      </span>
                    </div>
                    <span class="inline-block mt-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full"
                      :class="categoryColorClass(integration.category_key)">
                      {{ integration.category }}
                    </span>
                  </div>
                </div>

                <!-- Description -->
                <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2 flex-1">
                  {{ integration.description }}
                </p>
              </div>

              <!-- Card footer -->
              <div class="border-t border-gray-100 dark:border-gray-700/70 px-5 py-3 flex items-center justify-between">
                <component :is="actionButtonComponent(integration)"
                  v-bind="actionButtonAttrs(integration)"
                  @click="handleAction(integration)">
                  <svg v-if="loadingId === integration.integration_id" class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  <svg v-else-if="actionType(integration) === 'manage'" class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"/>
                  </svg>
                  <span>{{ actionLabel(integration) }}</span>
                </component>

                <!-- Coming-soon badge on right side of footer -->
                <span v-if="actionType(integration) === 'coming-soon'"
                  class="text-[11px] font-medium text-gray-400 dark:text-gray-500">
                  Not yet available
                </span>
              </div>
            </div>
          </div>
        </template>

      </div>
    </div>
    <!-- ══ END DESKTOP ══ -->

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { useIntegrationsStore } from 'stores/integrations.js'
import { useRequest } from 'composables/useRequest.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'

const ZOOM_OAUTH_URL =
  'https://zoom.us/oauth/authorize?response_type=code&client_id=xaA7iC1yQ13BVxuT2vv3w&redirect_uri=https%3A%2F%2Fweekler.co%2Finteg%2Fauth'

const store = useIntegrationsStore()
const flash = ref(null)
const { axiosRequest } = useRequest(flash)

// ── UI state ──
const searchQuery     = ref('')
const selectedCategory = ref('')
const loadingId       = ref(null)
const disconnecting   = ref(false)
const confirmModal    = ref({ open: false, integration: null })

// ── Computed ──
const uniqueCategories = computed(() => {
  const seen = new Map()
  store.integrations.forEach((i) => {
    if (!seen.has(i.category_key)) seen.set(i.category_key, i.category)
  })
  return Array.from(seen.entries())
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
})

const filteredIntegrations = computed(() => {
  let list = store.integrations
  if (selectedCategory.value) {
    list = list.filter((i) => i.category_key === selectedCategory.value)
  }
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    list = list.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q),
    )
  }
  return list
})

// ── Helpers ──
function categoryColorClass(key) {
  const map = {
    payments:      'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400',
    communication: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400',
    application:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400',
    system:        'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
  }
  return map[key] ?? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
}

function actionType(integration) {
  if (['external_payments', 'webhooks'].includes(integration.locale_name)) return 'manage'
  if (integration.locale_name === 'zoom') {
    return integration.user_integration_id ? 'disconnect-zoom' : 'connect-zoom'
  }
  if (integration.single_click_enable) {
    return integration.user_integration_id ? 'disconnect' : 'connect'
  }
  return 'coming-soon'
}

function actionLabel(integration) {
  if (loadingId.value === integration.integration_id) return '...'
  const type = actionType(integration)
  if (type === 'manage')           return 'Manage'
  if (type === 'connect-zoom')     return 'Connect'
  if (type === 'disconnect-zoom')  return 'Disconnect'
  if (type === 'connect')          return 'Connect'
  if (type === 'disconnect')       return 'Disconnect'
  return 'Coming soon'
}

function manageUrl(integration) {
  if (integration.locale_name === 'webhooks') return '/integrations/webhooks'
  if (integration.locale_name === 'external_payments') return '/integrations/external_payments'
  return null
}

function actionButtonComponent(integration) {
  return actionType(integration) === 'manage' ? RouterLink : 'button'
}

function actionButtonAttrs(integration) {
  const type = actionType(integration)
  const isLoading = loadingId.value === integration.integration_id

  const base = 'inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 transition-all'

  if (isLoading) {
    return {
      class: `${base} opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800 text-gray-400`,
      disabled: true,
    }
  }

  if (type === 'manage') {
    return {
      to: manageUrl(integration),
      'data-turbo-prefetch': 'false',
      class: `${base} text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40`,
    }
  }

  if (type === 'connect' || type === 'connect-zoom') {
    return {
      class: `${base} text-white bg-blue-600 hover:bg-blue-700 shadow-sm`,
    }
  }

  if (type === 'disconnect' || type === 'disconnect-zoom') {
    return {
      class: `${base} text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-800 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20`,
    }
  }

  // coming-soon
  return {
    class: `${base} text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed`,
    disabled: true,
  }
}

// ── Actions ──
function handleAction(integration) {
  const type = actionType(integration)
  if (type === 'connect')          return doConnect(integration)
  if (type === 'disconnect')       return openConfirm(integration)
  if (type === 'connect-zoom')     return connectZoom()
  if (type === 'disconnect-zoom')  return openConfirm(integration)
  // 'manage' is handled by RouterLink — no click handler needed
}

function connectZoom() {
  window.location.href = ZOOM_OAUTH_URL
}

async function doConnect(integration) {
  loadingId.value = integration.integration_id
  const res = await axiosRequest(
    'POST',
    `/integrations/${integration.integration_id}/activate`,
    {},
    false,
  )
  loadingId.value = null
  if (!res.js_error && res.response.success) {
    store.setAll(res.response.data.current_user_integrations)
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show(res.response?.message || 'Failed to connect.', 'alert')
  }
}

function openConfirm(integration) {
  confirmModal.value = { open: true, integration }
}

async function executeDisconnect() {
  const integration = confirmModal.value.integration
  if (!integration) return

  disconnecting.value = true

  if (integration.locale_name === 'zoom') {
    const res = await axiosRequest('GET', '/zoom/rtoken', {}, false)
    disconnecting.value = false
    confirmModal.value.open = false
    if (!res.js_error && res.response.success) {
      store.setAll(res.response.data.current_user_integrations)
      flash.value?.show(res.response.message, 'notice')
    } else {
      flash.value?.show(res.response?.message || 'Failed to disconnect.', 'alert')
    }
    return
  }

  const res = await axiosRequest(
    'DELETE',
    `/integrations/${integration.user_integration_id}/deactivate`,
    {},
    false,
  )
  disconnecting.value = false
  confirmModal.value.open = false
  if (!res.js_error && res.response.success) {
    store.setAll(res.response.data.current_user_integrations)
    flash.value?.show(res.response.message, 'notice')
  } else {
    flash.value?.show(res.response?.message || 'Failed to disconnect.', 'alert')
  }
}

function clearFilters() {
  searchQuery.value = ''
  selectedCategory.value = ''
}

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
}

// ── Lifecycle ──
onMounted(() => {
  store.fetchIfStale()
})
</script>

<style scoped>
.no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }

.modal-enter-active { transition: opacity 0.2s ease; }
.modal-leave-active { transition: opacity 0.15s ease; }
.modal-enter-from,
.modal-leave-to   { opacity: 0; }
.modal-enter-active .modal-card { transition: transform 0.2s ease; }
.modal-enter-from  .modal-card  { transform: scale(0.96) translateY(8px); }
</style>
